import { useState, useEffect } from 'react';
import type { CategorizedNews, CategoryData, SortedEvent } from '@/services/gpt';
import type { FeedItem } from '@/types/api';
import type { LoadingStep } from '@/pages/FeedPage';
import styles from './CategorizedFeed.module.css';

interface CategorizedFeedProps {
  data: CategorizedNews | null;
  loadingStep: LoadingStep;
  articleCount: number;
  locationName: string;
}

const INITIAL_DISPLAY_COUNT = 3;

const STEP_MESSAGES: Record<string, string> = {
  fetching: 'Connecting to news sources...',
  scanning: 'Scanning latest content...',
  safety: 'Checking nearby safety alerts...',
  generating: 'AI is analyzing and categorizing...',
};

function LoadingAnimation({ currentStep, articleCount, locationName }: {
  currentStep: LoadingStep;
  articleCount: number;
  locationName: string;
}) {
  const [visibleSteps, setVisibleSteps] = useState<string[]>([]);

  useEffect(() => {
    if (currentStep && STEP_MESSAGES[currentStep]) {
      setVisibleSteps(prev => {
        if (prev.includes(currentStep)) return prev;
        const newSteps = [...prev, currentStep];
        // Keep only last 4 steps
        return newSteps.slice(-4);
      });
    }
  }, [currentStep]);

  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingHeader}>
        <h2 className={styles.loadingTitle}>
          {locationName ? `Analyzing ${locationName}` : 'Analyzing Local News'}
        </h2>
        <p className={styles.loadingSubtitle}>
          {articleCount > 0 ? `Processing ${articleCount} articles` : '\u00A0'}
        </p>
      </div>

      <div className={styles.stepsWrapper}>
        <div className={styles.stepsContainer}>
          {visibleSteps.map((step, index) => {
            const isLatest = index === visibleSteps.length - 1;
            const position = visibleSteps.length - 1 - index; // 0 for latest, 1, 2, 3 for older

            return (
              <div
                key={step}
                className={`${styles.step} ${isLatest ? styles.stepActive : styles.stepOld}`}
                style={{
                  '--position': position,
                } as React.CSSProperties}
              >
                <div className={styles.stepIcon}>
                  {isLatest ? (
                    <div className={styles.stepSpinner} />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span className={styles.stepLabel}>
                  {(STEP_MESSAGES[step] || step).replace('...', '')}
                  {isLatest && <span className={styles.stepDots} />}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface CategorySectionProps {
  title: string;
  icon: React.ReactNode;
  data: CategoryData;
  variant: 'safety' | 'urgent' | 'important' | 'informational';
}

function CategorySection({ title, icon, data, variant }: CategorySectionProps) {
  const [expanded, setExpanded] = useState(false);

  const totalItems = data.points.reduce((sum, p) => sum + p.items.length, 0) + data.otherItems.length;

  if (totalItems === 0 && data.points.length === 0) {
    return null;
  }

  const hasOtherItems = data.otherItems.length > 0;
  // Safety: collapse all otherItems by default; Others: show first 3
  const initialCount = variant === 'safety' ? 0 : INITIAL_DISPLAY_COUNT;
  const displayOtherItems = expanded ? data.otherItems : data.otherItems.slice(0, initialCount);
  const hasMoreOthers = data.otherItems.length > initialCount;
  const hasPoints = data.points.length > 0;

  return (
    <section className={`${styles.section} ${styles[variant]}`}>
      <div className={styles.sectionBox}>
        <div className={styles.headerTop}>
          <div className={styles.sectionIcon}>{icon}</div>
          <h2 className={styles.sectionTitle}>{title}</h2>
          <span className={styles.sectionCount}>{totalItems}</span>
        </div>

        {hasPoints && (
          <div className={styles.pointsList}>
            {variant === 'safety' ? (
              // Safety: render clickable cards with summary
              data.points.map((point, index) => (
                point.items[0] && (
                  <SafetyCard
                    key={index}
                    summary={point.text}
                    item={point.items[0]}
                  />
                )
              ))
            ) : (
              // Other categories: render summary text with article cards
              data.points.map((point, index) => (
                <div key={index} className={styles.summaryPoint}>
                  <p className={styles.pointText}>{point.text}</p>
                  {point.items.length > 0 && (
                    <div className={styles.pointCards}>
                      {point.items.map((item) => (
                        <MiniCard key={item.id} item={item} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {hasOtherItems && (
          <div className={hasPoints ? styles.otherItems : styles.otherItemsOnly}>
            {hasPoints && variant !== 'safety' && <div className={styles.otherItemsHeader}>More</div>}
            <div className={styles.sectionGrid}>
              {displayOtherItems.map((item) => (
                <MiniCard key={item.id} item={item} />
              ))}
            </div>
            {hasMoreOthers && (
              <button
                className={styles.moreButton}
                onClick={() => setExpanded(!expanded)}
                translate="no"
              >
                <span translate="no">{expanded ? 'Show Less' : `Show ${data.otherItems.length - initialCount} More`}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {expanded ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function MiniCard({ item }: { item: FeedItem }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.miniCard}
    >
      {item.image && (
        <div className={styles.miniCardImage}>
          <img src={item.image} alt="" loading="lazy" />
        </div>
      )}
      <div className={styles.miniCardContent}>
        <h4 className={styles.miniCardTitle}>{item.title}</h4>
        <div className={styles.miniCardMeta}>
          <span className={styles.miniCardSource}>{item.source}</span>
          <span className={styles.miniCardDate}>{item.formattedDate}</span>
        </div>
      </div>
    </a>
  );
}

function SafetyCard({ summary, item }: { summary: string; item: FeedItem }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.safetyCard}
    >
      {item.image && (
        <div className={styles.safetyCardImage}>
          <img src={item.image} alt="" loading="lazy" />
        </div>
      )}
      <div className={styles.safetyCardContent}>
        <p className={styles.safetyCardSummary}>{summary}</p>
        <div className={styles.safetyCardMeta}>
          <span className={styles.safetyCardSource}>{item.source}</span>
          <span className={styles.safetyCardDate}>{item.formattedDate}</span>
        </div>
      </div>
    </a>
  );
}

function EventCard({ event }: { event: SortedEvent }) {
  const { item, relevanceScore, reason } = event;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.eventCard}
    >
      {item.image && (
        <div className={styles.eventCardImage}>
          <img src={item.image} alt="" loading="lazy" />
        </div>
      )}
      <div className={styles.eventCardContent}>
        <div className={styles.eventCardHeader}>
          <h4 className={styles.eventCardTitle}>{item.title}</h4>
          <span className={styles.eventRelevanceScore}>{relevanceScore}</span>
        </div>
        <p className={styles.eventCardReason}>{reason}</p>
        <div className={styles.eventCardMeta}>
          <span className={styles.eventCardSource}>{item.source}</span>
          <span className={styles.eventCardDate}>{item.formattedDate}</span>
        </div>
      </div>
    </a>
  );
}

interface EventsSectionProps {
  events: SortedEvent[];
  holidayContext: string | null;
}

function EventsSection({ events, holidayContext }: EventsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const INITIAL_COUNT = 5;

  if (events.length === 0) {
    return null;
  }

  const displayEvents = expanded ? events : events.slice(0, INITIAL_COUNT);
  const hasMore = events.length > INITIAL_COUNT;

  return (
    <section className={`${styles.section} ${styles.events}`}>
      <div className={styles.sectionBox}>
        <div className={styles.headerTop}>
          <div className={styles.sectionIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
            </svg>
          </div>
          <h2 className={styles.sectionTitle}>Local Events</h2>
          <span className={styles.sectionCount}>{events.length}</span>
        </div>

        {holidayContext && (
          <div className={styles.holidayBanner}>
            <span className={styles.holidayIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
            {holidayContext}
          </div>
        )}

        <div className={styles.eventsList}>
          {displayEvents.map((event, index) => (
            <EventCard key={event.item.id || index} event={event} />
          ))}
        </div>

        {hasMore && (
          <button
            className={styles.moreButton}
            onClick={() => setExpanded(!expanded)}
            translate="no"
          >
            <span translate="no">{expanded ? 'Show Less' : `Show ${events.length - INITIAL_COUNT} More`}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {expanded ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
            </svg>
          </button>
        )}
      </div>
    </section>
  );
}

export function CategorizedFeed({ data, loadingStep, articleCount, locationName }: CategorizedFeedProps) {
  const [showContent, setShowContent] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const isLoading = loadingStep !== 'idle' && loadingStep !== 'error' && (loadingStep !== 'complete' || !data);

  useEffect(() => {
    if (!isLoading && data) {
      // Start fade out of loading
      setFadeOut(true);
      // After fade out, show content
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      setFadeOut(false);
    }
  }, [isLoading, data]);

  // Show loading when: still loading OR fading out (before content shows)
  if (isLoading || (fadeOut && !showContent)) {
    return (
      <div className={styles.container}>
        <div className={fadeOut ? styles.fadeOut : ''}>
          <LoadingAnimation
            currentStep={loadingStep}
            articleCount={articleCount}
            locationName={locationName}
          />
        </div>
      </div>
    );
  }

  if (!data || !showContent) {
    return null;
  }

  const countItems = (cat: CategoryData) =>
    cat.points.reduce((sum, p) => sum + p.items.length, 0) + cat.otherItems.length;

  const eventsCount = data.events?.events?.length || 0;
  const totalItems = countItems(data.safety) + countItems(data.urgent) + countItems(data.important) + countItems(data.informational) + eventsCount;

  if (totalItems === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>
          <p>No local stories found for this area.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      <CategorySection
        title="Safety"
        variant="safety"
        data={data.safety}
        icon={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
        }
      />

      <CategorySection
        title="Heads Up"
        variant="urgent"
        data={data.urgent}
        icon={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <circle cx="12" cy="17" r="1" fill="currentColor" />
          </svg>
        }
      />

      <CategorySection
        title="Important"
        variant="important"
        data={data.important}
        icon={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
        }
      />

      <CategorySection
        title="News & Updates"
        variant="informational"
        data={data.informational}
        icon={
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14,2 14,8 20,8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        }
      />

      {data.events && data.events.events.length > 0 && (
        <EventsSection
          events={data.events.events}
          holidayContext={data.events.holidayContext}
        />
      )}
    </div>
  );
}
